// src/components/Admin/GroupsList.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from '../../components/UserDialog/ConfirmDialog';
import axios from 'axios';

import { AgGridReact } from 'ag-grid-react';
import {
  ClientSideRowModelModule,
  ClipboardModule,
  RichSelectModule,
  ExcelExportModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
  ModuleRegistry,
} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ClipboardModule,
  ExcelExportModule,
  RichSelectModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  RowStyleModule,
  RowSelectionModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  PaginationModule,
  CheckboxEditorModule,
  RenderApiModule,
]);

export default function GroupsList() {
  const gridRef = useRef(null);

  // ── Main “Groups” table state ──
  const [allPermissions, setAllPermissions] = useState([]); // used in Add/Edit Group dialog
  const [rowData, setRowData] = useState([]); // groups nested data
  const [loading, setLoading] = useState(true);

  // ── “Add/Edit Group” dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentGroup, setCurrentGroup] = useState({
    id: '',
    name: '',
    permissions: [],
    assignType: 'NETSUITE',
    assignedProjects: [],
  });

  // ── “Edit Permissions” dialog state ──
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [newPermName, setNewPermName] = useState('');
  const [loadingPerms, setLoadingPerms] = useState(true);
  const [allPermList, setAllPermList] = useState([]);

  // ── “Confirm Delete Group” dialog state ──
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [groupToDelete, setGroupToDelete] = useState(null);

  // ── On mount: load group‐list  permissions for toggles ──
  useEffect(() => {
    const loadMetadata = async () => {
      await fetchPermissions();
      await fetchGroups();
    };
    loadMetadata();
  }, []);

  // ── Fetch only “non-agent” permissions (for the Add/Edit Group toggles) ──
  const fetchPermissions = async () => {
    try {
      const { data } = await axios.get('/api/Admin/user-permissions-basic');
      const permsNotSparrowAgents = data.filter(
        (p) => !p.name.toLowerCase().includes('agent')
      );
      permsNotSparrowAgents.sort((a, b) => a.name.localeCompare(b.name));
      setAllPermissions(permsNotSparrowAgents);
    } catch (err) {
      console.error('Failed to fetch permissions', err);
      setAllPermissions([]);
    }
  };

  // ── Fetch “user‐groups‐nested” to populate the main grid ──
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/Admin/user-groups-nested');
      setRowData(data);
    } catch (err) {
      console.error('Failed to fetch groups', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  // ── When “Edit” is clicked, open the Add/Edit Group dialog ──
  const handleEditClick = (groupRow) => {
    setIsEditMode(true);
    const found = rowData.find((g) => g.groupId === groupRow.groupId);
    if (!found) return;
    const permIds = (found.accessPermissions || []).map((p) => p.permissionGroupId);
    setCurrentGroup({
      id: found.groupId,
      name: found.name,
      permissions: permIds,
      assignType: found.projectAccessDependant ? 'MANUAL' : 'NETSUITE',
      hasSparrowAccess: found.hasSparrowAccess,
      assignedProjects: [],
    });
    setDialogOpen(true);
  };

  // ── Show ConfirmDialog when trash‐icon is clicked ──
  const handleDeleteClick = (groupRow) => {
    setGroupToDelete(groupRow);
    setConfirmMessage(`Are you sure you want to delete group “${groupRow.name}”?`);
    setConfirmOpen(true);
  };
  const onConfirmDelete = async () => {
    if (!groupToDelete) {
      setConfirmOpen(false);
      return;
    }
    try {
      await axios.delete(`/api/Admin/group/${groupToDelete.groupId}`);
      await fetchGroups();
    } catch (err) {
      console.error(`Failed to delete group ${groupToDelete.groupId}`, err);
    } finally {
      setConfirmOpen(false);
      setGroupToDelete(null);
    }
  };
  const onCancelDelete = () => {
    setConfirmOpen(false);
    setGroupToDelete(null);
  };

  // ── Toggle a permission in the Add/Edit Group dialog ──
  const togglePermission = (permId) => {
    setCurrentGroup((prev) => {
      const alreadyHas = prev.permissions.includes(permId);
      const newPerms = alreadyHas
        ? prev.permissions.filter((p) => p !== permId)
        : [...prev.permissions, permId];

      const assignedProjectsPermissionId = allPermissions.find(
        (p) => p.name === 'ASSIGNED_PROJECTS'
      )?.permissionId;
      const assignedSparrowPermissionId = allPermissions.find(
        (p) => p.name === 'SPARROW_AI_ACCESS'
      )?.permissionId;

      const hasAssignedProjects = Boolean(
        assignedProjectsPermissionId && newPerms.includes(assignedProjectsPermissionId)
      );
      const newHasSparrowAccess = Boolean(
        assignedSparrowPermissionId && newPerms.includes(assignedSparrowPermissionId)
      );

      if (!hasAssignedProjects) {
        return {
          ...prev,
          permissions: newPerms,
          assignType: 'NETSUITE',
          assignedProjects: [],
          projectAccessDependant: false,
          hasSparrowAccess: newHasSparrowAccess,
        };
      }

      return {
        ...prev,
        permissions: newPerms,
        hasSparrowAccess: newHasSparrowAccess,
      };
    });
  };

  // ── Save the group (create or update) ──
  const handleSaveGroup = async () => {
    const assignedProjectsPermissionId = allPermissions.find(
      (p) => p.name === 'ASSIGNED_PROJECTS'
    )?.permissionId;
    const assignedSparrowPermissionId = allPermissions.find(
      (p) => p.name === 'SPARROW_AI_ACCESS'
    )?.permissionId;

    const hasAssignedProjects = Boolean(
      assignedProjectsPermissionId && currentGroup.permissions.includes(assignedProjectsPermissionId)
    );
    const hasSparrowAccess = Boolean(
      assignedSparrowPermissionId && currentGroup.permissions.includes(assignedSparrowPermissionId)
    );

    const payload = {
      ...(isEditMode && { groupId: currentGroup.id }),
      name: currentGroup.name.trim(),
      assignedPermissionIds: currentGroup.permissions.slice(),
      projectAccessDependant: hasAssignedProjects && currentGroup.assignType === 'MANUAL',
      hasSparrowAccess,
    };

    try {
      if (isEditMode) {
        await axios.put(`/api/Admin/edit-group`, payload);
      } else {
        await axios.post(`/api/Admin/add-group`, payload);
      }
      await fetchGroups();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save group', err);
    }
  };

  // ── Fetch full permission list (excluding “agent” or “sparrow-access”) ──
  const fetchAllPermsForEditing = async () => {
    setLoadingPerms(true);
    try {
      const { data } = await axios.get('/api/Admin/user-permissions-basic');
      const filtered = data.filter(
        (p) => !p.name.toLowerCase().includes('agent')
      );
      filtered.sort((a, b) => a.name.localeCompare(b.name));
      setAllPermList(filtered.map((p) => ({ id: p.permissionId, name: p.name })));
    } catch (err) {
      console.error('Failed to fetch full permission list', err);
      setAllPermList([]);
    } finally {
      setLoadingPerms(false);
    }
  };

  // ── Add a brand‐new permission from the “Edit Permissions” dialog ──
  const handleAddNewPermission = async () => {
    const trimmed = newPermName.trim().toUpperCase();
    if (!trimmed) return;
    try {
      await axios.post(
        `/api/Admin/add-permission?name=${encodeURIComponent(trimmed)}`
      );
      setNewPermName('');
      await fetchAllPermsForEditing();
      await fetchPermissions();
    } catch (err) {
      console.error('Failed to add new permission', err);
    }
  };

  // ── Delete an existing permission from “Edit Permissions” dialog ──
  const handleDeletePermission = async (permId) => {
    try {
      await axios.delete(`/api/Admin/permission/${permId}`);
      await fetchAllPermsForEditing();
      await fetchPermissions();
    } catch (err) {
      console.error(`Failed to delete permission ${permId}`, err);
    }
  };

  // ── AG Grid column definitions, including “Group Users” ──
  const columnDefs = useMemo(
    () => [
      { headerName: 'ID', field: 'groupId', hide: true },

      {
        headerName: 'Actions',
        field: 'actions',
        minWidth: 120,
        cellRenderer: (params) => (
          <Box>
            <IconButton size="small" onClick={() => handleEditClick(params.data)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => handleDeleteClick(params.data)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {
        headerName: 'Group Name',
        field: 'name',
        flex: 1,
        cellStyle: { textAlign: 'left' },
        sort: 'asc',
      },
      {
        headerName: 'Permissions',
        field: 'accessPermissions',
        flex: 2,
        cellStyle: { textAlign: 'left' },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (params) => {
          const permsArray = params.value || [];
          if (permsArray.length === 0) return 'None';
          return permsArray.map((p) => p.name).join(', ');
        },
        cellRenderer: (params) => {
          const permsArray = params.data.accessPermissions || [];
          if (permsArray.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#888' }}
              >
                None
              </Typography>
            );
          }
          return (
            <Box>
              {permsArray.map((p) => (
                <Typography key={p.permissionGroupId} variant="body2">
                  {p.name}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        headerName: 'Group Users',
        field: 'userGroupUsers',
        flex: 2,
        cellStyle: { textAlign: 'left', whiteSpace: 'normal' },
        autoHeight: true,
        wrapText: true,
        valueFormatter: (params) => {
          const usersArray = params.value || [];
          if (usersArray.length === 0) return 'None';
          return usersArray.map((u) => u.name).join(', ');
        },
        cellRenderer: (params) => {
          const usersArray = params.value || [];
          if (usersArray.length === 0) {
            return (
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#888' }}
              >
                None
              </Typography>
            );
          }
          return (
            <Box
              sx={{
                columnCount: 2,
                columnGap: 12,
                // NOTE: no fixed height or overflow here
              }}
            >
              {usersArray.map((u) => (
                <Typography
                  key={u.id}
                  variant="body2"
                  sx={{ breakInside: 'avoid-column', mb: 1 }}
                >
                  {u.name}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        headerName: 'Project-Assignment Dependant',
        field: 'projectAccessDependant',
        flex: 1,
        cellStyle: { textAlign: 'left' },
      },
      {
        headerName: 'Has Sparrow Access',
        field: 'hasSparrowAccess',
        flex: 1,
        cellStyle: { textAlign: 'left' },
      },
    ],
    [allPermissions, rowData]
  );

  const openAddDialog = () => {
    setIsEditMode(false);
    setCurrentGroup({
      id: '',
      name: '',
      permissions: [],
      assignType: 'NETSUITE',
      assignedProjects: [],
      projectAccessDependant: false,
      hasSparrowAccess: false,
    });
    setDialogOpen(true);
  };

  return (
    <Box>
      {/* ── Header: “Add Group”  “Edit Permissions” ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
          gap: 1,
        }}
      >
        <Button variant="contained" onClick={openAddDialog}>
          Add Group
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            setNewPermName('');
            fetchAllPermsForEditing();
            setPermDialogOpen(true);
          }}
        >
          Edit Permissions
        </Button>
      </Box>

      {/* ── Main AG Grid ── */}
      <div style={{ height: '74vh', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          rowSelection={{
            mode: 'multiRow',
            checkboxes: false,
            headerCheckbox: false,
            suppressRowClickSelection: true,
          }}
          animateRows
          loading={loading}
        />
      </div>

      {/* ── “Add/Edit Group” Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isEditMode ? 'Edit Group' : 'Add Group'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Group Name"
              name="name"
              value={currentGroup.name}
              onChange={(e) =>
                setCurrentGroup((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />

            <Divider />

            <Typography variant="subtitle1">Permissions</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {allPermissions.map((perm) => {
                const isAssignedProjectPerm = perm.name === 'ASSIGNED_PROJECTS';
                const checked = currentGroup.permissions.includes(
                  perm.permissionId
                );
                return (
                  <Box
                    key={perm.permissionId}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={checked}
                          onChange={() =>
                            togglePermission(perm.permissionId)
                          }
                        />
                      }
                      label={perm.name}
                      sx={{ flexGrow: 1 }}
                    />

                    {isAssignedProjectPerm && checked && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentGroup.assignType === 'MANUAL'}
                            onChange={(e) =>
                              setCurrentGroup((prev) => ({
                                ...prev,
                                assignType: e.target.checked
                                  ? 'MANUAL'
                                  : 'NETSUITE',
                              }))
                            }
                          />
                        }
                        label={
                          currentGroup.assignType === 'MANUAL'
                            ? 'Manual'
                            : 'Netsuite'
                        }
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveGroup}
            disabled={!currentGroup.name.trim()}
          >
            {isEditMode ? 'Save Changes' : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── “Edit Permissions” Dialog ── */}
      <Dialog
        open={permDialogOpen}
        onClose={() => setPermDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Permissions</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 1) Add new permission */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="New Permission Name"
                value={newPermName}
                onChange={(e) => setNewPermName(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleAddNewPermission}
                disabled={!newPermName.trim()}
              >
                Add
              </Button>
            </Box>

            <Divider />
            {/* 2) Scrollable list of allPermList, each with delete icon */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: 'auto',
                mt: 1,
              }}
            >
              {loadingPerms ? (
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', mt: 2 }}
                >
                  Loading…
                </Typography>
              ) : allPermList.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', color: '#888', p: 1 }}
                >
                  No permissions found.
                </Typography>
              ) : (
                <List dense>
                  {allPermList.map((perm) => (
                    <ListItem key={perm.id} sx={{ py: 0, px: 1 }}>
                      <ListItemText primary={perm.name} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDeletePermission(perm.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── ConfirmDialog for deletions ── */}
      <ConfirmDialog
        open={confirmOpen}
        message={confirmMessage}
        onConfirm={onConfirmDelete}
        onCancelClick={onCancelDelete}
        onClose={onCancelDelete}
        showCancel={true}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </Box>
  );
}
